import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useForm } from 'react-hook-form';
import { useManagedMutation } from 'utils/managedHooks';
import { useHistory } from 'react-router';
import {
  DISCOUNT_STATION_DIRECTION_BOTH,
  DISCOUNT_STATION_DIRECTION_DESTINATION,
  DISCOUNT_STATION_DIRECTION_ORIGIN,
  VoucherStatus
} from 'constants/vouchers';
import moment from 'moment';
import DiscountCodeDetailsTable from './DiscountCodeDetailsTable';
import {
  ExpandableContentCell,
  ExpandableContentGrid1Cell,
  ExpandableContentGrid2Cell,
  ExpandableContentGrid3Cell,
  ExpandableContentHeaderCell,
  StyledH2
} from './DiscountCodeDetails.styles';
import DiscountCodeDetailExpandableItem from './DiscountCodeDetailExpandableItem';
import { withStations } from '../../components/queries/GetStationsQuery';
import { DiscountEditSettings } from './hooks/useDiscountEditSettings';
import UpdateDiscountCTA from './UpdateDiscountCta/UpdateDiscountCta';
import UPDATE_VOUCHER from './mutations/UPDATE_VOUCHER';
import { enableUpdateVoucher } from './DiscountCodeDetails.utils';
import DiscountUpdateModalReasons from './DiscountUpdateModalReasons';

const DiscountCodeDetails = ({
  createdFor,
  businessAccount,
  reference,
  voucherAlias,
  type,
  valueType,
  voucherOriginalValue,
  voucherCurrency,
  voucherStatus,
  salesPeriodStart,
  salesPeriodEnd,
  travelPeriodStart,
  travelPeriodEnd,
  travelDays,
  createdBy,
  createdAt,
  updatedBy,
  updatedAt,
  activatedAt,
  inactivatedAt,
  email,
  canDisableVoucher,
  canEnableVoucher,
  canUpdateVoucher,
  travelPeriodExceptions,
  salesChannelRestrictions,
  countryRestrictions,
  productRestrictions,
  isActive,
  settings,
  stationRestrictions,
  maxTotalDiscountAmount,
  minBookingValue,
  maxBookingValue,
  stations,
  payments,
  canUpdateSilverVouchers,
  canEnableSilverVouchers,
  canDisableSilverVouchers
}) => {
  const history = useHistory();
  const { register, handleSubmit, formState, reset, watch } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      email: email || '',
      voucherStatus: VoucherStatus[voucherStatus]
    }
  });

  const statusValue = watch('voucherStatus');
  const [updateVoucher, { loading }] = useManagedMutation(UPDATE_VOUCHER);
  const [isEditMode, setEditMode] = useState(false);
  const [isConfirmationOpened, setConfirmationOpened] = useState(false);
  const isUpdateDiscountDisabled = !enableUpdateVoucher({ salesPeriodEnd, canEnableSilverVouchers, canDisableSilverVouchers, payments });
  const isConfirmationEnabled = formState.isValid && formState.isDirty && statusValue !== VoucherStatus[voucherStatus];

  const onEditSubmit = () => {
    setConfirmationOpened(true);
  };

  const onConfirmSubmit = async ({ reason }) => {
    await updateVoucher({
      variables: {
        voucherAlias,
        reason,
        active: statusValue === VoucherStatus.ACTIVE
      }
    });
    history.go();
  };

  const onCancelEdit = () => {
    reset();
    setEditMode(false);
  };

  const getStations = (direction) =>
    stationRestrictions
      .filter((sr) => sr.role === direction)
      .map((sr) => {
        const station = stations.find((s) => s.uic === sr.stationUicCode);

        return {
          uic: station?.uic,
          name: station?.name
        };
      });

  return (
    <DiscountEditSettings
      voucherType={type}
      voucherStatus={voucherStatus}
      isEditMode={isEditMode}
      canEditDiscounts={canUpdateSilverVouchers}
      canDisableSilverVouchers={canDisableSilverVouchers}
      canEnableSilverVouchers={canEnableSilverVouchers}
    >
      <DiscountUpdateModalReasons
        isOpen={isConfirmationOpened}
        isLoading={loading}
        onToggle={() => setConfirmationOpened(!isConfirmationOpened)}
        onSubmit={onConfirmSubmit}
        showUpdateWarnings
      />

      <form data-cy="discount-form" onSubmit={handleSubmit(onEditSubmit)}>
        <StyledH2 paddingTop="30px">
          <FormattedMessage id="discountCodes.discountCodeDetails" />
        </StyledH2>

        <DiscountCodeDetailsTable
          businessAccount={businessAccount}
          createdFor={createdFor}
          reference={reference}
          voucherAlias={voucherAlias}
          type={type}
          valueType={valueType}
          voucherCurrency={voucherCurrency}
          voucherOriginalValue={voucherOriginalValue}
          voucherStatus={voucherStatus}
          salesPeriodStart={salesPeriodStart}
          salesPeriodEnd={salesPeriodEnd}
          travelPeriodStart={travelPeriodStart}
          travelPeriodEnd={travelPeriodEnd}
          travelDays={travelDays}
          createdBy={createdBy}
          createdAt={createdAt}
          updatedBy={updatedBy}
          updatedAt={updatedAt}
          activatedAt={activatedAt}
          inactivatedAt={inactivatedAt}
          canDisableVoucher={canDisableVoucher}
          canEnableVoucher={canEnableVoucher}
          canUpdateVoucher={canUpdateVoucher}
          register={register}
          isActive={isActive}
          maxTotalDiscountAmount={maxTotalDiscountAmount}
          minBookingValue={minBookingValue}
          maxBookingValue={maxBookingValue}
        />

        <DiscountCodeDetailExpandableItem title="discountCodes.travelPeriodExceptions">
          <ExpandableContentGrid2Cell>
            <ExpandableContentHeaderCell>
              <FormattedMessage id="discountCodes.from" />
            </ExpandableContentHeaderCell>
            <ExpandableContentHeaderCell>
              <FormattedMessage id="discountCodes.to" />
            </ExpandableContentHeaderCell>
            {travelPeriodExceptions.map((t) => (
              <>
                <ExpandableContentCell>{moment.utc(t.startDate).format('DD/MM/YYYY')}</ExpandableContentCell>
                <ExpandableContentCell>{moment.utc(t.endDate).format('DD/MM/YYYY')}</ExpandableContentCell>
              </>
            ))}
          </ExpandableContentGrid2Cell>
        </DiscountCodeDetailExpandableItem>

        <DiscountCodeDetailExpandableItem title="discountCodes.salesChannelRestrictions">
          <ExpandableContentGrid1Cell>
            {salesChannelRestrictions?.includedSalesChannels?.map((t) => (
              <ExpandableContentCell key={t.salesChannelCode}>{t.salesChannelCode}</ExpandableContentCell>
            ))}
          </ExpandableContentGrid1Cell>
        </DiscountCodeDetailExpandableItem>

        <DiscountCodeDetailExpandableItem title="discountCodes.countryRestrictions">
          <ExpandableContentGrid1Cell>
            {countryRestrictions.map((t) => (
              <ExpandableContentCell key={t}>{t}</ExpandableContentCell>
            ))}
          </ExpandableContentGrid1Cell>
        </DiscountCodeDetailExpandableItem>

        <DiscountCodeDetailExpandableItem title="discountCodes.productRestrictions">
          <ExpandableContentGrid1Cell>
            {productRestrictions.map((t) => (
              <ExpandableContentCell key={t.productCode}>{t.productCode}</ExpandableContentCell>
            ))}
          </ExpandableContentGrid1Cell>
        </DiscountCodeDetailExpandableItem>

        <DiscountCodeDetailExpandableItem title="discountCodes.permittedStations">
          <ExpandableContentGrid3Cell>
            <ExpandableContentHeaderCell>
              <FormattedMessage id="discountCodes.origin" />
            </ExpandableContentHeaderCell>
            <ExpandableContentHeaderCell>
              <FormattedMessage id="discountCodes.destination" />
            </ExpandableContentHeaderCell>
            <ExpandableContentHeaderCell>
              <FormattedMessage id="discountCodes.both" />
            </ExpandableContentHeaderCell>

            <ExpandableContentCell>
              {getStations(DISCOUNT_STATION_DIRECTION_ORIGIN).map((s) => (
                <div key={s.uic}>{s.name}</div>
              ))}
            </ExpandableContentCell>
            <ExpandableContentCell>
              {getStations(DISCOUNT_STATION_DIRECTION_DESTINATION).map((s) => (
                <div key={s.uic}>{s.name}</div>
              ))}
            </ExpandableContentCell>
            <ExpandableContentCell>
              {getStations(DISCOUNT_STATION_DIRECTION_BOTH).map((s) => (
                <div key={s.uic}>{s.name}</div>
              ))}
            </ExpandableContentCell>
          </ExpandableContentGrid3Cell>
        </DiscountCodeDetailExpandableItem>

        <DiscountCodeDetailExpandableItem title="discountCodes.settings">
          <ExpandableContentGrid2Cell>
            <ExpandableContentCell>
              <FormattedMessage id="discountCodes.usageType" />
            </ExpandableContentCell>
            <ExpandableContentCell>{settings.usageType}</ExpandableContentCell>

            <ExpandableContentCell>
              <FormattedMessage id="discountCodes.usageLimit" />
            </ExpandableContentCell>
            <ExpandableContentCell>{settings.usageLimit}</ExpandableContentCell>

            <ExpandableContentCell>
              <FormattedMessage id="discountCodes.spendingRestrictionType" />
            </ExpandableContentCell>
            <ExpandableContentCell>{settings.spendingRestrictionType}</ExpandableContentCell>

            <ExpandableContentCell>
              <FormattedMessage id="discountCodes.minRequiredGroupSize" />
            </ExpandableContentCell>
            <ExpandableContentCell>{settings.minRequiredGroupSize}</ExpandableContentCell>

            <ExpandableContentCell>
              <FormattedMessage id="discountCodes.maxRequiredGroupSize" />
            </ExpandableContentCell>
            <ExpandableContentCell>{settings.maxRequiredGroupSize}</ExpandableContentCell>

            <ExpandableContentCell>
              <FormattedMessage id="discountCodes.usageRestrictionType" />
            </ExpandableContentCell>
            <ExpandableContentCell>{settings.usageRestrictionType}</ExpandableContentCell>

            <ExpandableContentCell>
              <FormattedMessage id="discountCodes.tripRestrictionType" />
            </ExpandableContentCell>
            <ExpandableContentCell>{settings.tripRestrictionType}</ExpandableContentCell>
          </ExpandableContentGrid2Cell>
        </DiscountCodeDetailExpandableItem>

        {canUpdateSilverVouchers && (
          <UpdateDiscountCTA
            onEditClick={() => setEditMode(true)}
            onCancelClick={onCancelEdit}
            isEditMode={isEditMode}
            isConfirmEnabled={isConfirmationEnabled}
            disabled={isUpdateDiscountDisabled || loading}
          />
        )}
      </form>
    </DiscountEditSettings>
  );
};

DiscountCodeDetails.propTypes = {
  createdFor: PropTypes.string,
  businessAccount: PropTypes.string,
  reference: PropTypes.string,
  voucherAlias: PropTypes.string,
  type: PropTypes.string,
  valueType: PropTypes.string,
  voucherOriginalValue: PropTypes.number,
  voucherCurrency: PropTypes.string,
  voucherStatus: PropTypes.string,
  salesPeriodStart: PropTypes.string,
  salesPeriodEnd: PropTypes.string,
  travelPeriodStart: PropTypes.string,
  travelPeriodEnd: PropTypes.string,
  travelDays: PropTypes.string,
  createdBy: PropTypes.string,
  createdAt: PropTypes.string,
  updatedBy: PropTypes.string,
  updatedAt: PropTypes.string,
  activatedAt: PropTypes.string,
  inactivatedAt: PropTypes.string,
  email: PropTypes.string,
  canDisableVoucher: PropTypes.bool,
  canEnableVoucher: PropTypes.bool,
  canUpdateVoucher: PropTypes.bool,
  travelPeriodExceptions: PropTypes.arrayOf(
    PropTypes.shape({
      startDate: PropTypes.string,
      endDate: PropTypes.string
    })
  ),
  salesChannelRestrictions: PropTypes.shape({
    includedSalesChannels: PropTypes.arrayOf(
      PropTypes.shape({
        salesChannelCode: PropTypes.string
      })
    )
  }),
  countryRestrictions: PropTypes.arrayOf(PropTypes.string),
  productRestrictions: PropTypes.arrayOf(
    PropTypes.shape({
      productCode: PropTypes.string
    })
  ),
  isActive: PropTypes.bool,
  settings: PropTypes.shape({
    usageType: PropTypes.string,
    usageLimit: PropTypes.number,
    spendingRestrictionType: PropTypes.string,
    minRequiredGroupSize: PropTypes.number,
    maxRequiredGroupSize: PropTypes.number,
    usageRestrictionType: PropTypes.string,
    tripRestrictionType: PropTypes.string
  }),
  stationRestrictions: PropTypes.arrayOf(
    PropTypes.shape({
      stationUicCode: PropTypes.string,
      role: PropTypes.string
    })
  ),
  maxTotalDiscountAmount: PropTypes.number,
  minBookingValue: PropTypes.number,
  maxBookingValue: PropTypes.number,
  stations: PropTypes.arrayOf(
    PropTypes.shape({
      __typename: PropTypes.string,
      isRdg: PropTypes.bool,
      name: PropTypes.string,
      priority: PropTypes.number,
      searchStrings: PropTypes.arrayOf(PropTypes.string),
      shortCode: PropTypes.string,
      uic: PropTypes.string
    })
  ),
  payments: PropTypes.shape({
    amount: PropTypes.string,
    currency: PropTypes.string,
    status: PropTypes.string,
    bookingReference: PropTypes.string
  }),
  canUpdateSilverVouchers: PropTypes.bool,
  canEnableSilverVouchers: PropTypes.bool,
  canDisableSilverVouchers: PropTypes.bool
};

export default withStations(DiscountCodeDetails);
